import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { useDashboardPath } from '#client/hooks/useDashboardPath';

const BUSINESS_FINANCE_TAX_CLASSIFICATION_QUERY = gql`
  query BusinessFinanceTaxClassification($businessEntityUuid: UUID!) {
    businessFinanceTaxClassification(businessEntityUuid: $businessEntityUuid)
  }
`;

/**
 * Component that checks if the business entity has a tax classification
 * and redirects to the redirect path if the condition is met.
 * @param {object} props
 * @param {Function} [props.shouldRedirect] - A conditional function that returns a truthy value to trigger the redirection.
 * @param {string} [props.redirectPath] - The absolute path to redirect to if the condition is met (will be processed by useDashboardPath).
 * @param {import('react').ReactNode} [props.children] - The children to render.
 * @component
 */
export function TaxClassificationGate({ shouldRedirect = () => false, redirectPath = '/products', children }) {
  const { businessEntityUuid } = useBusinessEntity();
  const dashboardRedirectPath = useDashboardPath(redirectPath);

  const { data, loading, error } = useQuery(
    BUSINESS_FINANCE_TAX_CLASSIFICATION_QUERY,
    {
      noThrow: true,
      skip: !businessEntityUuid,
      variables: {
        businessEntityUuid
      }
    }
  );

  useError(error);
  // If there are errors, redirect as the product cannot be determined or accessed for this business entity
  if (error) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  if (loading || !data) {
    return <PageLoading />;
  }

  if (shouldRedirect(data.businessFinanceTaxClassification)) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  return children;
}

TaxClassificationGate.propTypes = {
  shouldRedirect: PropTypes.func,
  redirectPath: PropTypes.string,
  children: PropTypes.node.isRequired
};
